import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Download } from 'lucide-react';
import { useState } from 'react';
import { AmountHeads, HeadCells, MonthPicker, Section } from '@/components/gst/return-parts';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import {
  downloadReturnJson,
  lastMonth,
  monthLabel,
  periodRange,
  stateLabel,
  useGstr3b,
  type GstHeads,
  type Gstr3bView,
} from '@/lib/gst';
import { i18n, useT } from '@/lib/i18n';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';

export const Route = createFileRoute('/taxes/gst/gstr3b')({
  validateSearch: (s: Record<string, unknown>): { period?: string } =>
    typeof s['period'] === 'string' && /^\d{4}-\d{2}$/.test(s['period'])
      ? { period: s['period'] }
      : {},
  component: Gstr3bPage,
});

/** Rows whose figures open the GST detail report filtered to them. */
const DRILL = new Set(['3.1a', '3.1b', '3.1c', '3.1d', '3.1e', '4A2', '4A3', '4A5', '4C']);

/** Row keys as the API sends them (tax pack GSTR3B_LABELS), keyed for the catalogue. */
const ROW_KEYS = {
  '3.1a': 'r31a',
  '3.1b': 'r31b',
  '3.1c': 'r31c',
  '3.1d': 'r31d',
  '3.1e': 'r31e',
  '4A1': 'r4A1',
  '4A2': 'r4A2',
  '4A3': 'r4A3',
  '4A4': 'r4A4',
  '4A5': 'r4A5',
  '4B1': 'r4B1',
  '4B2': 'r4B2',
  '4C': 'r4C',
  '4D1': 'r4D1',
  '4D2': 'r4D2',
} as const;
/** A GSTR-3B row's description in the reader's language (unknown rows show as sent). */
const rowLabel = (key: string, label: string): string =>
  key in ROW_KEYS ? i18n.t(`taxPages.gstr3b.row.${ROW_KEYS[key as keyof typeof ROW_KEYS]}`) : label;

/** Screen T8 (document 06): GSTR-3B for a month, in form order, with the payment of tax. */
function Gstr3bPage() {
  const t = useT();
  const orgId = useOrgId();
  const navigate = useNavigate();
  const period = Route.useSearch().period ?? lastMonth();
  const r = useGstr3b(period);
  const [busy, setBusy] = useState(false);
  const { from, to } = periodRange(period);

  return (
    <>
      <PageHeader
        title="GSTR-3B"
        description={t('taxPages.gstr3b.description')}
        actions={
          <>
            <MonthPicker
              period={period}
              onChange={(p) => void navigate({ to: '/taxes/gst/gstr3b', search: { period: p } })}
            />
            <Button asChild variant="outline">
              <Link
                to="/reports/$reportKey"
                params={{ reportKey: 'gstr-3b' }}
                search={{ from, to } as never}
              >
                {t('taxPages.gst.openAsReport')}
              </Link>
            </Button>
            <Button
              disabled={!r.data?.gstin || busy}
              title={r.data && !r.data.gstin ? t('taxPages.gst.addGstin') : undefined}
              onClick={async () => {
                setBusy(true);
                try {
                  await downloadReturnJson(orgId!, 'gstr3b', period, r.data!.gstin!);
                } catch (e) {
                  toast((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Download className="size-4" /> {t('taxPages.gst.downloadJson')}
            </Button>
          </>
        }
      />
      <p className="mb-4 text-sm">
        <Link to="/taxes/gst" className="text-primary hover:underline">
          {t('taxPages.gst.title')}
        </Link>{' '}
        › {monthLabel(period)}
        {r.data ? ` ${t('taxPages.gst.dueOn', { date: displayDate(r.data.dueDate) })}` : ''}
      </p>
      {r.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : r.isError ? (
        <EmptyState title={t('taxPages.gstr3b.loadError')} hint={r.error.message} />
      ) : (
        <Body r={r.data} from={from} to={to} />
      )}
    </>
  );
}

function Body({ r, from, to }: { r: Gstr3bView; from: string; to: string }) {
  const t = useT();
  const { currency } = useOrgLocale();
  const rows = (keys: string[]) => r.rows.filter((x) => keys.includes(x.key));
  const label = (key: string, text: string) =>
    DRILL.has(key) ? (
      <Link
        to="/reports/$reportKey"
        params={{ reportKey: 'gst-detail' }}
        search={{ from, to, types: key.toLowerCase() } as never}
        className="text-primary hover:underline"
      >
        ({key}) {text}
      </Link>
    ) : (
      <>
        ({key}) {text}
      </>
    );
  const table = (keys: string[], testId: string) => (
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('taxPages.gst.natureOfSupplies')}</TableHead>
          <AmountHeads />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows(keys).map((x) => (
          <TableRow key={x.key} className={x.key === '4C' ? 'font-semibold' : undefined}>
            <TableCell>{label(x.key, rowLabel(x.key, x.label))}</TableCell>
            <HeadCells a={x} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  const s = r.setOff;
  const headRow = (text: string, h: GstHeads, bold = false) => (
    <TableRow className={bold ? 'font-semibold' : undefined}>
      <TableCell>{text}</TableCell>
      <HeadCells a={{ ...h, taxable: '0' }} taxable={false} />
    </TableRow>
  );
  const cash = [s.cash.igst, s.cash.cgst, s.cash.sgst, s.cash.cess].reduce(
    (acc, v) => acc + (toMinor(v) ?? 0n),
    0n,
  );

  return (
    <div className="space-y-6">
      {r.findings.length ? (
        <div
          className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-sm"
          role="note"
        >
          <p className="mb-1 inline-flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-4 text-[#B45309]" aria-hidden />{' '}
            {t('taxPages.vatReturn.exceptionsTitle')}
          </p>
          <ul className="list-disc ps-5">
            {r.findings.map((f, n) => (
              <li key={n}>{f.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <Section title={t('taxPages.gstr3b.s31')}>
        {table(['3.1a', '3.1b', '3.1c', '3.1d', '3.1e'], 'gstr3b-31')}
      </Section>
      <Section title={t('taxPages.gstr3b.s32')}>
        {r.interState.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('taxPages.gst.placeOfSupply')}</TableHead>
                <TableHead className="w-[150px] text-end">
                  {t('taxPages.gst.taxableValue')}
                </TableHead>
                <TableHead className="w-[150px] text-end">IGST</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.interState.map((x) => (
                <TableRow key={x.placeOfSupply}>
                  <TableCell>{stateLabel(x.placeOfSupply)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(x.taxable)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(x.igst)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {t('taxPages.gstr3b.noneThisMonth')}
          </p>
        )}
      </Section>
      <Section title={t('taxPages.gstr3b.s4')}>
        {table(['4A1', '4A2', '4A3', '4A4', '4A5', '4B1', '4B2', '4C', '4D1', '4D2'], 'gstr3b-4')}
        <p className="border-t px-4 py-2 text-xs text-muted-foreground">
          {t('taxPages.gstr3b.s4Note')}
        </p>
      </Section>
      <Section title={t('taxPages.gstr3b.s5')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('taxPages.gst.natureOfSupplies')}</TableHead>
              <TableHead className="w-[150px] text-end">{t('taxPages.gst.interState')}</TableHead>
              <TableHead className="w-[150px] text-end">{t('taxPages.gst.intraState')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {r.exemptInward.map((x) => (
              <TableRow key={x.type}>
                <TableCell>
                  {x.type === 'GST'
                    ? t('taxPages.gstr3b.compositionExempt')
                    : t('taxPages.gstr3b.nonGstSupplies')}
                </TableCell>
                <TableCell className="tnum text-end">{formatAmount(x.inter)}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(x.intra)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title={t('taxPages.gstr3b.s61')} testId="gstr3b-payment">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('taxPages.vatReturn.colDescription')}</TableHead>
              <AmountHeads taxable={false} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {headRow(t('taxPages.gstr3b.taxPayable'), s.liability)}
            {headRow(t('taxPages.gstr3b.paidItcIgst'), s.paidBy.igst)}
            {headRow(t('taxPages.gstr3b.paidItcCgst'), s.paidBy.cgst)}
            {headRow(t('taxPages.gstr3b.paidItcSgst'), s.paidBy.sgst)}
            {headRow(t('taxPages.gstr3b.paidCash'), s.cash, true)}
            {headRow(t('taxPages.gstr3b.itcCarried'), s.carryForward)}
          </TableBody>
        </Table>
        <p className="border-t px-4 py-2 text-sm">
          {t('taxPages.gst.colCashPayable')}{' '}
          <span className="tnum font-semibold" data-testid="gstr3b-cash">
            {currency} {formatMinor(cash)}
          </span>
        </p>
      </Section>
    </div>
  );
}
